import type { GroupElementsRequest } from '@/api/types/group-elements-api'
import type { StowedElement } from '@/api/types/stowed-element'
import { IDENTITY_PROJECTION } from '@/lib/canvas/document-projection'
import {
  buildGroupElementsRequest,
  buildRestructureBodyWrappedMultiGroups,
} from '@/lib/groups/build-group-api-payloads'
import { isGroupShape } from '@/lib/groups/group-kind'
import { isDescendantOfGroup } from '@/lib/groups/shape-group-tree'
import { isTextUnderGroup } from '@/lib/groups/text-under-group'
import type { ForwardGroupApiPersistenceDeps } from './types/forward-group-api-persistence-deps'

/**
 * Persist a newly created kind-7 group (POST /group or POST /restructure), matching
 * `handleShapeCreated` when `isGroupTxn` — used after redo Group and after undo Ungroup.
 */
export function persistForwardGroupApiFromShapes(deps: ForwardGroupApiPersistenceDeps): void {
  const {
    shapes,
    textBoxes = [],
    group,
    insertIndex,
    historyTxnId,
    projection = IDENTITY_PROJECTION,
    markEdited,
    enqueueGroup,
    enqueueRestructure,
    warn,
  } = deps

  let restructureBody: StowedElement[] | null = null

  try {
    restructureBody = buildRestructureBodyWrappedMultiGroups(
      shapes,
      group.id,
      textBoxes,
      projection
    )
  } catch (e) {
    warn('persistForwardGroupApiFromShapes: failed to build wrap-multi-group restructure body', e)
  }

  if (restructureBody && restructureBody.length > 0) {
    const byId = new Map(shapes.map((s) => [s.id, s]))
    const edited = new Set<string>()

    for (const s of shapes) {
      if (s.id === group.id || isDescendantOfGroup(s, group.id, byId)) {
        edited.add(s.id)
      }
    }

    for (const tb of textBoxes) {
      if (isTextUnderGroup(tb, group.id, byId)) {
        edited.add(tb.id)
      }
    }

    void markEdited([...edited])
    enqueueRestructure({
      moves: [],
      meta: { historyTxnId },
      apiBody: restructureBody,
    })
    return
  }

  const childrenInZOrder = shapes.filter(
    (candidate) => candidate.parentGroupId === group.id && !isGroupShape(candidate)
  )
  const textChildrenInZOrder = textBoxes.filter((t) => t.parentGroupId === group.id)
  const childIds = [
    ...childrenInZOrder.map((candidate) => candidate.id),
    ...textChildrenInZOrder.map((t) => t.id),
  ]

  let apiBody: GroupElementsRequest | undefined

  try {
    apiBody = buildGroupElementsRequest(group, childrenInZOrder, textChildrenInZOrder, projection)
  } catch (e) {
    warn('persistForwardGroupApiFromShapes: failed to build group API body', e)
  }

  void markEdited([group.id, ...childIds])
  enqueueGroup({
    groupId: group.id,
    parentGroupId: group.parentGroupId,
    childIds,
    insertIndex,
    meta: { historyTxnId },
    ...(apiBody ? { apiBody } : {}),
  })
}
